/**
 * `skaile catalog` — Catalog source health and configuration commands.
 *
 * Phase 2 / Task 2.7 surface. Sub-commands:
 *
 * - `skaile catalog test` — probe the configured catalog source and report OK
 *   or a clear actionable error. In `catalog.url: local` mode, lists the
 *   registered local source instead. In air-gapped mode (`cache_ttl: 0`) the
 *   command does NOT touch the network; pass `--force` to probe anyway.
 *
 * Read-only diagnostics — does NOT mutate config or library state.
 *
 * @docLink cli/commands/catalog#make-catalog-command
 */

import path from "node:path";
import { Command } from "commander";
import { logErr, logInfo, logOk, logWarn, S } from "../helpers.ts";

/**
 * Creates the `skaile catalog` command group.
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/catalog#make-catalog-command
 */
export function makeCatalogCommand(): Command {
  const catalog = new Command("catalog").description("Catalog source health and configuration");

  catalog
    .command("test")
    .description("Probe the configured catalog source and report status")
    .option("--project-dir <path>", "Project directory (for project-level config overlay)")
    .option("--url <url>", "Override the catalog URL for this probe")
    .option("--force", "Probe the network even in air-gapped mode (cache_ttl: 0)")
    .action(async (opts: { projectDir?: string; url?: string; force?: boolean }) => {
      const { resolveConfig, isLocalCatalogUrl, trpcGetUrl } = await import(
        "@skaile/workspaces/library"
      );
      const projectDir = opts.projectDir ? path.resolve(opts.projectDir) : undefined;
      const cfg = resolveConfig({ projectDir });
      const baseUrl = opts.url ?? cfg.catalog.url;

      // ── Local mode ─────────────────────────────────────────────────────
      if (isLocalCatalogUrl(baseUrl)) {
        await runLocalCatalogTest(opts.projectDir);
        return;
      }

      // ── Air-gapped mode (cache_ttl: 0) — refuse network probe by default ──
      // The user has explicitly told us not to reach out. Honour that intent.
      if (cfg.catalog.cache_ttl === 0 && !opts.force) {
        logOk(`Catalog mode: air-gapped (${S.heading(baseUrl)})`);
        logInfo(
          "Network probe skipped because cache_ttl is 0. " +
            "Use `skaile update` to refresh the cache, or `skaile catalog test --force` to probe anyway.",
        );
        return;
      }

      // ── Remote mode — probe a real catalog tRPC route ──────────────────
      await runRemoteCatalogProbe(baseUrl, trpcGetUrl(baseUrl, "catalog.listKinds", undefined));
    });

  catalog
    .command("info")
    .description("Print the resolved catalog configuration (url, framing, cacheTtlMs)")
    .option("--project-dir <path>", "Project directory (for project-level config overlay)")
    .action(async (opts: { projectDir?: string }) => {
      const { resolveConfig } = await import("@skaile/workspaces/library");
      const projectDir = opts.projectDir ? path.resolve(opts.projectDir) : undefined;
      const cfg = resolveConfig({ projectDir });
      console.log(`url: ${cfg.catalog.url}`);
      console.log(`framing: ${cfg.catalog.framing}`);
      console.log(`cacheTtlMs: ${cfg.catalog.cache_ttl * 1000}`);
    });

  return catalog;
}

/**
 * Local-mode handler for `skaile catalog test`: list the registered local
 * catalog source instead of probing the network.
 */
async function runLocalCatalogTest(projectDir: string | undefined): Promise<void> {
  const { resolveCatalogSource } = await import("../open-library.ts");
  let resolved: Awaited<ReturnType<typeof resolveCatalogSource>> | undefined;
  try {
    resolved = await resolveCatalogSource({ projectDir });
    const sources = await resolved.source.listSources();
    logOk("Catalog mode: local");
    if (sources.length === 0) {
      logInfo("No local sources registered. Run `skaile source add <path>` first.");
    } else {
      for (const s of sources) {
        console.log(`  ${S.dim("•")} ${s.type} ${S.heading(s.url)}`);
      }
    }
  } catch (err) {
    logErr(err instanceof Error ? err.message : String(err));
    process.exitCode = 1;
  } finally {
    resolved?.close();
  }
}

/** True when `text` parses as a tRPC GET envelope (`{result: {…}}`). */
function isTrpcEnvelope(text: string): boolean {
  try {
    const body = JSON.parse(text);
    // tRPC GET shape: `{result: {data: {json: <payload>}}}`
    return (
      typeof body === "object" &&
      body !== null &&
      "result" in body &&
      typeof (body as { result: unknown }).result === "object"
    );
  } catch {
    return false;
  }
}

/**
 * Remote-mode handler for `skaile catalog test`: probe a real catalog tRPC
 * route and report status / set the exit code.
 *
 * We hit `catalog.listKinds` rather than `/health` because it exercises the
 * actual surface the CLI depends on. A working backend with the catalog router
 * unmounted (or behind a path prefix that doesn't match `~/.skaile/config.yaml`)
 * is exactly the failure we want to surface.
 */
async function runRemoteCatalogProbe(baseUrl: string, probeUrl: string): Promise<void> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 5_000);
  try {
    const started = Date.now();
    const res = await fetch(probeUrl, {
      signal: controller.signal,
      headers: { accept: "application/json" },
    });
    const ms = Date.now() - started;
    if (!res.ok) {
      logErr(`${probeUrl} responded ${res.status} ${res.statusText} (${ms}ms)`);
      if (res.status === 404) {
        logInfo(
          "Got 404 from catalog.listKinds. Check the URL prefix in ~/.skaile/config.yaml — " +
            "Skaile mirrors must expose `/trpc/catalog.*` at the configured baseUrl.",
        );
      } else {
        logInfo(
          "Verify the URL in ~/.skaile/config.yaml and any auth requirements on private mirrors.",
        );
      }
      process.exitCode = 1;
      return;
    }
    // Validate the response actually parses as a tRPC superjson envelope.
    const text = (await res.text()).trim();
    if (!isTrpcEnvelope(text)) {
      logWarn(
        `Catalog reachable at ${baseUrl} (${ms}ms) but the response is not a valid tRPC envelope.`,
      );
      logInfo(
        "The host responded 200 but the body is not Skaile's catalog router shape. " +
          "Check that `catalog.listKinds` is mounted at the configured baseUrl.",
      );
      process.exitCode = 1;
      return;
    }
    logOk(`Catalog reachable at ${S.heading(baseUrl)} (${ms}ms)`);
  } catch (err) {
    if (err instanceof Error && err.name === "AbortError") {
      logErr(`Timeout reaching ${probeUrl} after 5s`);
    } else {
      logErr(`Could not reach ${probeUrl}: ${err instanceof Error ? err.message : String(err)}`);
    }
    logInfo("Verify the URL in ~/.skaile/config.yaml and that the catalog backend is deployed.");
    process.exitCode = 1;
  } finally {
    clearTimeout(timeout);
  }
}
